import { useRef } from "react";
import clsx from "clsx";
import type { DayView, TripLeg } from "@poi-planner/shared";
import { useLegStore } from "../store/legStore";
import { useTripStore } from "../store/tripStore";
import { type PlannerMode, useUIStore } from "../store/uiStore";
import { addDaysToDate } from "../utils/zonedTime";
import { findTodayView } from "../utils/nowTime";
import { useNow } from "../hooks/useNow";
import { Button } from "./Button";
import { FormSelect } from "./FormFields";
import { PaneTitleButton } from "./PaneToggle";
import { SegmentedControl } from "./SegmentedControl";
import { CalendarAgendaView } from "./calendar/CalendarAgendaView";
import { CalendarMonthView } from "./calendar/CalendarMonthView";
import { CalendarWeekView } from "./calendar/CalendarWeekView";
import { NowButton } from "./calendar/NowButton";
import { phonePlannerMode } from "./phone/phoneLayout";

function formatDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

interface PlannerPaneProps {
  allDayViews: DayView[];
  visibleDayViews: DayView[];
  activeLeg: TripLeg | null;
  /** Omitted when there is no map to show places on (offline). */
  onShowOnMap?: (poiId: string) => void;
  onCollapse?: () => void;
  /**
   * Phone board: no title or Calendar view, and the day and leg navigation
   * share the header row with the view switcher.
   */
  compact?: boolean;
}

export function PlannerPane({
  allDayViews,
  visibleDayViews,
  activeLeg,
  onShowOnMap,
  onCollapse,
  compact = false,
}: PlannerPaneProps) {
  const trip = useTripStore((state) => state.trip);
  const isReadOnly = useTripStore((state) => state.isReadOnly());
  const legs = useLegStore((state) => state.legs);
  const createLeg = useLegStore((state) => state.createLeg);
  const requestedPlannerMode = useUIStore((state) => state.plannerMode);
  const setPlannerMode = useUIStore((state) => state.setPlannerMode);
  const plannerFocusDate = useUIStore((state) => state.plannerFocusDate);
  const setPlannerFocusDate = useUIStore((state) => state.setPlannerFocusDate);
  const setPlannerCalendarStart = useUIStore(
    (state) => state.setPlannerCalendarStart,
  );
  const setActiveLegId = useUIStore((state) => state.setActiveLegId);
  const openLegEditor = useUIStore((state) => state.openLegEditor);
  const now = useNow();
  const paneRef = useRef<HTMLElement>(null);

  if (!trip) return null;

  const plannerMode = compact
    ? phonePlannerMode(requestedPlannerMode)
    : requestedPlannerMode;

  const focusDate = plannerFocusDate ?? allDayViews[0]?.date ?? trip.startDate;
  const focusIndex = allDayViews.findIndex((day) => day.date === focusDate);
  const activeLegIndex = activeLeg
    ? legs.findIndex((leg) => leg.id === activeLeg.id)
    : -1;

  // While the trip is under way the header's Now button returns the view
  // to the current time. When today isn't rendered, these bring it in.
  const todayDate = findTodayView(allDayViews, now)?.date ?? null;
  const todayLeg = todayDate
    ? legs.find((leg) => leg.startDate <= todayDate && todayDate <= leg.endDate)
    : undefined;
  const navigateToToday = !todayDate
    ? undefined
    : plannerMode === "day"
      ? () => setPlannerFocusDate(todayDate)
      : plannerMode === "leg" && todayLeg
        ? () => setActiveLegId(todayLeg.id)
        : plannerMode === "calendar"
          ? () => {
              setPlannerFocusDate(todayDate);
              setPlannerCalendarStart(null);
            }
          : undefined;
  const nowButton = todayDate && (
    <NowButton
      containerRef={paneRef}
      markerKey={`${plannerMode}:${visibleDayViews[0]?.date ?? ""}:${visibleDayViews.at(-1)?.date ?? ""}:${todayDate}`}
      onNavigateToToday={navigateToToday}
      iconOnly={compact && (plannerMode === "day" || plannerMode === "leg")}
    />
  );

  const createFirstLeg = async () => {
    const leg = await createLeg(trip.id, {
      name: "New leg",
      startDate: focusDate,
      endDate: focusDate,
      color: 0,
    });
    if (!leg) return;
    setActiveLegId(leg.id);
    openLegEditor(leg.id);
  };

  const subtitle =
    plannerMode === "day"
      ? focusIndex >= 0
        ? `Day ${focusIndex + 1} of ${allDayViews.length}`
        : formatDate(focusDate)
      : plannerMode === "leg" && activeLeg
        ? `${activeLeg.name} · ${activeLeg.startDate}–${activeLeg.endDate}`
        : `${visibleDayViews.length} day${visibleDayViews.length === 1 ? "" : "s"}`;

  const modeSwitcher = (
    <SegmentedControl
      aria-label="Planner view"
      size={compact ? "sm" : "md"}
      value={plannerMode}
      onChange={(mode: PlannerMode) => setPlannerMode(mode)}
      options={[
        { value: "day", label: "Day" },
        { value: "leg", label: "Leg" },
        compact
          ? { value: "all", label: "All", ariaLabel: "All days" }
          : { value: "all", label: "All days" },
        ...(compact ? [] : [{ value: "calendar" as const, label: "Calendar" }]),
        { value: "agenda", label: "Agenda" },
      ]}
    />
  );

  const dayNav = (
    <div
      className={clsx(
        "flex items-center gap-1",
        compact && "min-w-0 flex-1 justify-end",
      )}
      aria-label="Day navigation"
    >
      <Button
        variant="icon"
        icon="chevron_left"
        aria-label="Previous day"
        title="Previous day"
        disabled={focusIndex <= 0}
        onClick={() => setPlannerFocusDate(addDaysToDate(focusDate, -1))}
      />
      <h3
        className={clsx(
          "m-0 text-center text-sm font-extrabold tracking-tight text-primary",
          compact ? "min-w-0 flex-1 truncate" : "min-w-40",
        )}
      >
        {formatDate(focusDate)}
      </h3>
      <Button
        variant="icon"
        icon="chevron_right"
        aria-label="Next day"
        title="Next day"
        disabled={focusIndex < 0 || focusIndex >= allDayViews.length - 1}
        onClick={() => setPlannerFocusDate(addDaysToDate(focusDate, 1))}
      />
    </div>
  );

  const legNav = legs.length > 0 && (
    <div
      className={clsx(
        "flex items-center gap-1",
        compact && "min-w-0 flex-1 justify-end",
      )}
      aria-label="Leg navigation"
    >
      {/* The phone header has room for the picker alone */}
      {!compact && (
        <Button
          variant="icon"
          icon="chevron_left"
          aria-label="Previous leg"
          title="Previous leg"
          disabled={activeLegIndex <= 0}
          onClick={() => setActiveLegId(legs[activeLegIndex - 1]?.id ?? null)}
        />
      )}
      <div className={clsx(compact && "min-w-0 flex-1")}>
        <FormSelect
          data-testid="planner-leg-select"
          aria-label="Active leg"
          value={activeLeg?.id ?? legs[0].id}
          onChange={setActiveLegId}
          options={legs.map((leg) => ({
            value: leg.id,
            label: leg.name,
          }))}
        />
      </div>
      {!compact && (
        <Button
          variant="icon"
          icon="chevron_right"
          aria-label="Next leg"
          title="Next leg"
          disabled={activeLegIndex < 0 || activeLegIndex >= legs.length - 1}
          onClick={() => setActiveLegId(legs[activeLegIndex + 1]?.id ?? null)}
        />
      )}
    </div>
  );

  return (
    <section
      ref={paneRef}
      className="flex min-h-0 min-w-0 flex-1 flex-col bg-page"
      aria-label="Planner"
      data-testid="planner-pane"
    >
      {compact ? (
        <div className="z-30 flex min-h-13 shrink-0 items-center gap-2 border-b border-border bg-white px-3 py-1.5">
          {modeSwitcher}
          {plannerMode === "day" ? (
            dayNav
          ) : plannerMode === "leg" && legNav ? (
            legNav
          ) : nowButton || plannerMode === "leg" ? (
            // Keeps the Now button at the right edge in every view, including
            // Leg mode before any legs exist
            <span className="flex-1" />
          ) : (
            <span className="min-w-0 flex-1 truncate text-right text-2xs text-tertiary">
              {subtitle}
            </span>
          )}
          {nowButton}
        </div>
      ) : (
        <>
          <div className="z-30 flex min-h-13 shrink-0 flex-wrap items-center gap-2 border-b border-border bg-white px-3 py-1.5">
            {/* A plain narrow-layout title yields to the view switcher; a
                toggle keeps its width and lets the switcher wrap instead */}
            <div
              className={clsx(
                "flex flex-1 basis-0 items-center",
                !onCollapse && "min-w-0",
              )}
            >
              <PaneTitleButton
                pane="planner"
                onCollapse={onCollapse}
                subtitle={subtitle}
              />
            </div>

            {modeSwitcher}

            {/* Balances the title so the view switcher stays centred; holds
                the Now button while the trip is under way */}
            <div className="flex flex-1 basis-0 items-center justify-end">
              {nowButton}
            </div>
          </div>

          {plannerMode === "day" && (
            <div className="flex shrink-0 justify-center bg-page px-3 pt-3">
              {dayNav}
            </div>
          )}

          {plannerMode === "leg" && legs.length > 0 && (
            <div className="flex shrink-0 justify-center bg-page px-3 pt-3">
              {legNav}
            </div>
          )}
        </>
      )}

      {plannerMode === "leg" && legs.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="material-symbols-outlined text-4xl text-tertiary">
            route
          </span>
          <div>
            <h3 className="text-sm font-bold text-primary">No legs yet</h3>
            <p className="mt-1 max-w-sm text-xs text-secondary">
              Create a leg to focus the Planner on a named date range.
            </p>
          </div>
          {!isReadOnly && (
            <Button variant="primary" icon="add" onClick={createFirstLeg}>
              Create leg
            </Button>
          )}
        </div>
      ) : plannerMode === "calendar" ? (
        <CalendarMonthView />
      ) : plannerMode === "agenda" ? (
        <CalendarAgendaView dayViews={visibleDayViews} />
      ) : (
        <CalendarWeekView
          dayViews={visibleDayViews}
          contextKey={`${plannerMode}:${visibleDayViews[0]?.date ?? "empty"}:${visibleDayViews.at(-1)?.date ?? "empty"}`}
          onShowOnMap={onShowOnMap}
        />
      )}
    </section>
  );
}
